/**
 * Contract templates: the "Templates" tab on the Contracts page.
 *
 * Templates are database records (`/admin/crm/contract-templates`), so they can
 * be edited and added from here. The list and the editor swap inside the same
 * tab without a route of their own; the editor lives in `TemplateEditor.tsx`.
 *
 * The rows do not navigate - they open the editor in place - so the title is a
 * `<button>` with a stretched overlay rather than a `<Link>`. What matters is
 * that it is a real control: the old `<tr onClick>` could not be reached by
 * keyboard and a screen reader had nothing to announce.
 */
import { useCallback, useEffect, useState } from "react";
import { FileText, Loader2, Lock, Plus } from "lucide-react";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import {
  deleteContractTemplate, getContractTemplate, getContractTemplates,
  type ContractTemplate, type ContractTemplateFull,
} from "../../../lib/projects";
import { Btn, EmptyState, StatusPill, Toolbar, ToolbarGap, listCard } from "../../ui";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { TemplateEditor } from "./TemplateEditor";
import { TemplateRowMenu } from "./TemplateRowMenu";
import { contractValue, locField } from "./lib";

/** Name, type, default value, term, actions. */
const COLS = "minmax(0,2.4fr) 1.1fr .9fr .9fr 44px";

export function TemplatesManager() {
  const t = useT();
  const { locale } = useI18n();
  const [rows, setRows] = useState<ContractTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  // undefined = showing the list, null = editing a new one, a record = editing it.
  const [editing, setEditing] = useState<ContractTemplateFull | null | undefined>(undefined);
  const [opening, setOpening] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<ContractTemplate | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await getContractTemplates()).templates);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The list carries a template without its body; the editor needs the body, so
  // opening one is a second fetch. The row shows a spinner while it is in
  // flight rather than opening an editor with empty fields.
  async function openEdit(tpl: ContractTemplate) {
    setOpening(tpl.key);
    try {
      setEditing(await getContractTemplate(tpl.key));
    } finally {
      setOpening(null);
    }
  }

  if (editing !== undefined) {
    return (
      <TemplateEditor
        initial={editing}
        onClose={() => setEditing(undefined)}
        onSaved={() => {
          setEditing(undefined);
          void load();
        }}
      />
    );
  }

  return (
    <>
      <Toolbar>
        <span className="crm-sub px-1.5">{t("templates.intro")}</span>
        <ToolbarGap />
        <Btn tone="brand" onClick={() => setEditing(null)}>
          <Plus className="size-4" />
          {t("templates.add")}
        </Btn>
      </Toolbar>

      <div className="crm-card" style={listCard(COLS)}>
        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={t("templates.empty")}
            body={t("templates.emptyBody")}
            action={
              <Btn tone="brand" onClick={() => setEditing(null)}>
                <Plus className="size-4" />
                {t("templates.add")}
              </Btn>
            }
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
              <span>{t("templates.name")}</span>
              <span>{t("templates.type")}</span>
              <span>{t("templates.defaultValue")}</span>
              <span>{t("templates.duration")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {rows.map((tpl) => {
                const name = locField(tpl.name, locale);
                const description = locField(tpl.description, locale);
                return (
                  <div key={tpl.key} className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
                    <span className="min-w-0 pl-3">
                      <span className="flex min-w-0 items-center gap-2">
                        {/* Opens the editor in place, so a button rather than a
                            link - but a real control either way, which the old
                            <tr onClick> was not. */}
                        <button
                          type="button"
                          onClick={() => void openEdit(tpl)}
                          className="crm-prow-n crm-trunc min-w-0 text-left after:absolute after:inset-0 after:rounded-full"
                        >
                          {name}
                        </button>
                        {opening === tpl.key ? (
                          <Loader2 className="crm-mut size-3.5 shrink-0 animate-spin" />
                        ) : null}
                        {tpl.builtin ? (
                          <StatusPill tone="mut">
                            <Lock className="size-3" />
                            {t("templates.builtin")}
                          </StatusPill>
                        ) : null}
                      </span>
                      {description ? <span className="crm-prow-s crm-trunc block">{description}</span> : null}
                    </span>

                    <span className="crm-mut crm-trunc text-[12.5px]">{locField(tpl.type, locale) || "-"}</span>

                    <span className="crm-tnum text-[13.25px] font-bold">
                      {contractValue(tpl.defaultValueCents)}
                    </span>

                    <span className="crm-mut crm-tnum text-[12.5px]">
                      {tpl.durationMonths ? t("templates.months", { n: tpl.durationMonths }) : "-"}
                    </span>

                    <TemplateRowMenu
                      template={tpl}
                      onEdit={() => void openEdit(tpl)}
                      onDelete={() => setConfirmDel(tpl)}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {confirmDel ? (
        <ConfirmModal
          title={t("templates.deleteTitle")}
          body={t("templates.deleteConfirm", { name: locField(confirmDel.name, locale) })}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            await deleteContractTemplate(confirmDel.key);
            setConfirmDel(null);
            void load();
          }}
          onClose={() => setConfirmDel(null)}
        />
      ) : null}
    </>
  );
}
